import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RewardsWalletQuery, RewardsWalletService, WalletGroupItem } from '@fgb/core';
import { firstValueFrom, Observable } from 'rxjs';

const DRAG_THRESHOLD = 6;
const FLICK_VELOCITY = 0.35;
const FLICK_IDLE_TIME = 120;

@Component({
  selector: 'fgb-wallet-page',
  templateUrl: './wallet-page.component.html',
  styleUrls: ['./wallet-page.component.scss'],
})
export class WalletPageComponent implements OnInit, AfterViewInit, OnDestroy {
  walletItems$: Observable<WalletGroupItem[]>;
  isCollapsed = false;

  @ViewChild('walletHeader') private headerRef?: ElementRef<HTMLElement>;
  @ViewChild('walletCollapsibleInner') private collapsibleInnerRef?: ElementRef<HTMLElement>;

  private contentHeight = 0;
  private dragOffset = 0;
  private dragStartY: number | null = null;
  private lastMoveY = 0;
  private lastMoveTime = 0;
  private velocity = 0;
  private isDragging = false;
  private suppressClick = false;
  private resizeObserver?: ResizeObserver;
  private readonly abort = new AbortController();

  constructor(
    private rewardsWalletQuery: RewardsWalletQuery,
    private rewardsWalletService: RewardsWalletService,
    private zone: NgZone
  ) {}

  async ngOnInit() {
    firstValueFrom(this.rewardsWalletService.fetchWalletItems());
    this.walletItems$ = this.rewardsWalletQuery.selectGroupedUnredeemedWalletItems();
  }

  ngAfterViewInit(): void {
    const header = this.headerRef?.nativeElement;
    const inner = this.collapsibleInnerRef?.nativeElement;
    if (!header || !inner) {
      return;
    }
    this.measure();
    const signal = this.abort.signal;
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(inner);
      header.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
      header.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
      header.addEventListener('touchend', (event) => this.onTouchEnd(event), { signal });
      header.addEventListener('touchcancel', (event) => this.onTouchEnd(event), { signal });
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.abort.abort();
  }

  onSheetEdgeClick(): void {
    if (!this.suppressClick && this.contentHeight > 0) {
      this.snapTo(!this.isCollapsed);
    }
  }

  private measure(): void {
    const height = this.collapsibleInnerRef?.nativeElement.offsetHeight ?? 0;
    if (height === this.contentHeight) {
      return;
    }
    this.contentHeight = height;
    const header = this.headerRef?.nativeElement;
    header?.classList.toggle('is-collapsible', height > 0);
    header?.style.setProperty('--wallet-collapsible-height', `${height}px`);
    this.dragOffset = this.isCollapsed ? height : 0;
    this.applyOffset();
  }

  private onTouchStart(event: TouchEvent): void {
    this.suppressClick = false;
    if (this.contentHeight === 0 || event.touches.length !== 1) {
      return;
    }
    this.dragStartY = event.touches[0].clientY;
    this.lastMoveY = this.dragStartY;
    this.lastMoveTime = event.timeStamp;
    this.velocity = 0;
  }

  private onTouchMove(event: TouchEvent): void {
    if (this.dragStartY === null) {
      return;
    }
    const y = event.touches[0].clientY;
    const delta = y - this.dragStartY;
    if (!this.isDragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) {
        return;
      }
      const canMove = delta < 0 ? this.dragOffset < this.contentHeight : this.dragOffset > 0;
      if (!canMove) {
        this.dragStartY = null;
        return;
      }
      this.isDragging = true;
      this.headerRef?.nativeElement.classList.add('is-dragging');
    }
    event.preventDefault();
    const elapsed = event.timeStamp - this.lastMoveTime;
    if (elapsed > 0) {
      this.velocity = (y - this.lastMoveY) / elapsed;
    }
    this.lastMoveY = y;
    this.lastMoveTime = event.timeStamp;
    const start = this.isCollapsed ? this.contentHeight : 0;
    this.dragOffset = Math.min(this.contentHeight, Math.max(0, start - delta));
    this.applyOffset();
  }

  private onTouchEnd(event: TouchEvent): void {
    if (this.dragStartY === null) {
      return;
    }
    const dragged = this.isDragging;
    const resting = event.timeStamp - this.lastMoveTime > FLICK_IDLE_TIME;
    this.dragStartY = null;
    this.isDragging = false;
    this.headerRef?.nativeElement.classList.remove('is-dragging');
    if (!dragged) {
      return;
    }
    this.suppressClick = true;
    const flicked = !resting && Math.abs(this.velocity) > FLICK_VELOCITY;
    const collapse = flicked ? this.velocity < 0 : this.dragOffset > this.contentHeight / 2;
    this.zone.run(() => this.snapTo(collapse));
  }

  private snapTo(collapsed: boolean): void {
    this.isCollapsed = collapsed;
    this.dragOffset = collapsed ? this.contentHeight : 0;
    this.headerRef?.nativeElement.classList.add('is-animated');
    this.applyOffset();
  }

  private applyOffset(): void {
    const header = this.headerRef?.nativeElement;
    if (!header) {
      return;
    }
    const progress = this.contentHeight > 0 ? this.dragOffset / this.contentHeight : 0;
    header.style.setProperty('--wallet-collapse-offset', `${this.dragOffset}px`);
    header.style.setProperty('--wallet-collapse-progress', `${progress}`);
  }
}
